import { airlineName } from './brands';
import { distanceMi, EARTH_CIRCUMFERENCE_MI, type LatLon } from './geo';
import type { PlaceLookup } from './items';
import type { FlightDetails, FlightLive, Item } from './schema';
import { isValidTimeZone, localDate, localParts } from './time';

/**
 * Flight stats for the Stats tab: every number is computed here from plain records, so the
 * screen only draws. Two steps: `flightRecords` reads each past flight once (places, live
 * timing, seat, class), then `flightStats` adds them up for all time or one year.
 */

/** Average distance from the Earth to the Moon. */
export const MOON_MI = 238_855;
/** Sovereign countries: the 193 UN members plus the Vatican and Palestine. */
export const COUNTRIES_TOTAL = 195;

export type Alliance = 'oneworld' | 'Star Alliance' | 'SkyTeam';
export type CabinClass = 'economy' | 'premium' | 'business' | 'first';
export type SeatPosition = 'window' | 'middle' | 'aisle';
export type Season = 'spring' | 'summer' | 'autumn' | 'winter';
export type Continent =
  | 'Africa'
  | 'Asia'
  | 'Europe'
  | 'North America'
  | 'South America'
  | 'Oceania'
  | 'Antarctica';

export const CABIN_LABELS: Record<CabinClass, string> = {
  economy: 'Economy',
  premium: 'Premium economy',
  business: 'Business',
  first: 'First',
};

/** Current members by marketing carrier code. Past membership (SAS in Star) isn't tracked. */
const ALLIANCE_MEMBERS: Record<Alliance, string> = {
  oneworld: 'AA AS AT AY BA CX FJ HA IB JL MH QF QR RJ UL WY',
  'Star Alliance': 'A3 AC AI AV BR CA CM ET LH LO LX MS NH NZ OS OU OZ SA SN SQ TG TK TP UA ZH',
  SkyTeam: 'AF AM AR CI DL GA KE KL KQ ME MF MU RO SK SV UX VN VS',
};

const ALLIANCE_BY_CODE = new Map<string, Alliance>();
for (const [name, codes] of Object.entries(ALLIANCE_MEMBERS) as [Alliance, string][]) {
  for (const c of codes.split(' ')) ALLIANCE_BY_CODE.set(c, name);
}

export function allianceOf(airline: string): Alliance | null {
  return ALLIANCE_BY_CODE.get(airline.trim().toUpperCase()) ?? null;
}

/** Countries and territories by continent (Turkey and Cyprus in Asia, Russia in Europe). */
const CONTINENT_CODES: Record<Continent, string> = {
  Africa:
    'DZ AO BJ BW BF BI CV CM CF TD KM CG CD CI DJ EG GQ ER SZ ET GA GM GH GN GW KE LS LR LY MG MW ML MR MU YT MA MZ NA NE NG RE RW SH ST SN SC SL SO ZA SS SD TZ TG TN UG EH ZM ZW',
  Asia: 'AF AM AZ BH BD BT BN KH CN CY GE HK IN ID IR IQ IL JO JP KZ KW KG LA LB MO MY MV MN MM NP KP OM PK PS PH QA SA SG KR LK SY TW TJ TH TL TR TM AE UZ VN YE IO',
  Europe:
    'AX AL AD AT BY BE BA BG HR CZ DK EE FO FI FR DE GI GR GG HU IS IE IM IT JE XK LV LI LT LU MT MD MC ME NL MK NO PL PT RO RU SM RS SK SI ES SJ SE CH UA GB VA',
  'North America':
    'AI AG AW BS BB BZ BM BQ VG CA KY CR CU CW DM DO SV GL GD GP GT HT HN JM MQ MX MS NI PA PR BL KN LC MF PM VC SX TT TC US VI',
  'South America': 'AR BO BR CL CO EC FK GF GY PY PE SR UY VE GS',
  Oceania: 'AS AU CK FJ PF GU KI MH FM NR NC NZ NU NF MP PW PG PN WS SB TK TO TV UM VU WF',
  Antarctica: 'AQ BV HM TF',
};

/** Sovereign countries per continent, matching COUNTRIES_TOTAL. */
export const CONTINENT_TOTALS: Record<Continent, number> = {
  Africa: 54,
  Asia: 48,
  Europe: 44,
  'North America': 23,
  'South America': 12,
  Oceania: 14,
  Antarctica: 0,
};

const CONTINENT_BY_CODE = new Map<string, Continent>();
for (const [name, codes] of Object.entries(CONTINENT_CODES) as [Continent, string][]) {
  for (const c of codes.split(' ')) CONTINENT_BY_CODE.set(c, name);
}

export function continentOf(countryCode: string): Continent | null {
  return CONTINENT_BY_CODE.get(countryCode.toUpperCase()) ?? null;
}

/**
 * US territories count as the US, so San Juan from Miami is a domestic flight and doesn't
 * add a country.
 */
const TERRITORY_OF: Record<string, string> = {
  PR: 'US',
  VI: 'US',
  GU: 'US',
  AS: 'US',
  MP: 'US',
  UM: 'US',
};

export function sovereignOf(countryCode: string): string {
  const cc = countryCode.toUpperCase();
  return TERRITORY_OF[cc] ?? cc;
}

/** The flag emoji for a two-letter country code, from regional indicator symbols. */
export function flagEmoji(countryCode: string): string {
  const cc = countryCode.toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

/**
 * Cabin from whatever the airline or TripIt wrote: 'Main Cabin', 'Comfort+', 'Delta One',
 * 'Premium Select'. Extra-legroom economy stays economy. Unknown text gives null.
 */
export function cabinClass(cabin: string | null | undefined): CabinClass | null {
  const c = cabin?.trim().toLowerCase();
  if (!c) return null;
  if (/premium|world traveller plus|economy delight/.test(c)) return 'premium';
  if (/business|delta one|polaris|mint|club world|upper class|club europe|\bclub\b/.test(c))
    return 'business';
  if (/first|suite|la premi/.test(c)) return 'first';
  if (/economy|coach|main|basic|comfort|standard|\beco\b|plus|extra|even more|preferred/.test(c))
    return 'economy';
  return null;
}

/** Common ICAO type codes, as live data reports them. */
const AIRCRAFT_CODES: Record<string, string> = {
  A19N: 'Airbus A319neo',
  A20N: 'Airbus A320neo',
  A21N: 'Airbus A321neo',
  A318: 'Airbus A318',
  A319: 'Airbus A319',
  A320: 'Airbus A320',
  A321: 'Airbus A321',
  A332: 'Airbus A330-200',
  A333: 'Airbus A330-300',
  A339: 'Airbus A330-900',
  A343: 'Airbus A340-300',
  A346: 'Airbus A340-600',
  A359: 'Airbus A350-900',
  A35K: 'Airbus A350-1000',
  A388: 'Airbus A380',
  BCS1: 'Airbus A220-100',
  BCS3: 'Airbus A220-300',
  B712: 'Boeing 717',
  B737: 'Boeing 737-700',
  B738: 'Boeing 737-800',
  B739: 'Boeing 737-900',
  B37M: 'Boeing 737 MAX 7',
  B38M: 'Boeing 737 MAX 8',
  B39M: 'Boeing 737 MAX 9',
  B3XM: 'Boeing 737 MAX 10',
  B744: 'Boeing 747-400',
  B748: 'Boeing 747-8',
  B752: 'Boeing 757-200',
  B753: 'Boeing 757-300',
  B763: 'Boeing 767-300',
  B764: 'Boeing 767-400',
  B772: 'Boeing 777-200',
  B77L: 'Boeing 777-200LR',
  B77W: 'Boeing 777-300ER',
  B788: 'Boeing 787-8',
  B789: 'Boeing 787-9',
  B78X: 'Boeing 787-10',
  CRJ2: 'Bombardier CRJ200',
  CRJ7: 'Bombardier CRJ700',
  CRJ9: 'Bombardier CRJ900',
  E170: 'Embraer 170',
  E75L: 'Embraer 175',
  E75S: 'Embraer 175',
  E190: 'Embraer 190',
  E195: 'Embraer 195',
  E290: 'Embraer E190-E2',
  E295: 'Embraer E195-E2',
  AT72: 'ATR 72',
  AT76: 'ATR 72',
  DH8D: 'Dash 8-400',
  MD88: 'McDonnell Douglas MD-88',
  MD90: 'McDonnell Douglas MD-90',
};

/** A tidy aircraft name: 'B738' and 'Boeing 737-800 (Winglets)' both read 'Boeing 737-800'. */
export function aircraftName(raw: string | null | undefined): string | null {
  const t = raw?.trim();
  if (!t) return null;
  const code = AIRCRAFT_CODES[t.toUpperCase()];
  if (code) return code;
  const name = t
    .replace(/\(.*?\)/g, '')
    .replace(/\b(passenger|pax|jet|aircraft)\b.*$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
  return name || null;
}

const WIDEBODY = /A3[03-8]\d|A380|7[4-8]7|MD-?11|DC-?10|L-?1011/i;

/**
 * Window, middle or aisle from the seat letter. Narrow-bodies seat A to F (A and F at the
 * windows); wide-bodies run to K or L, with the windows at A and the last letter.
 * A letter past F means a wide-body even when the aircraft is unknown. Best effort.
 */
export function seatPosition(
  seat: string | null | undefined,
  aircraft?: string | null,
): SeatPosition | null {
  const m = /^\s*\d{1,3}\s*([A-L])\s*$/i.exec(seat ?? '');
  if (!m) return null;
  const letter = m[1]!.toUpperCase();
  const wide = (aircraft != null && WIDEBODY.test(aircraft)) || letter > 'F';
  if (!wide) {
    if (letter === 'A' || letter === 'F') return 'window';
    if (letter === 'B' || letter === 'E') return 'middle';
    return 'aisle';
  }
  // 3-4-3 (ABC DEFG HJK) and 3-3-3 (ABC DEF GHK) both put windows at A and K or L.
  if (letter === 'A' || letter === 'K' || letter === 'L') return 'window';
  if (letter === 'C' || letter === 'D' || letter === 'G' || letter === 'H') return 'aisle';
  return 'middle';
}

/** Season of a local month, flipped south of the equator. */
export function seasonOf(month: number, southern = false): Season {
  const north: Season[] = [
    'winter',
    'winter',
    'spring',
    'spring',
    'spring',
    'summer',
    'summer',
    'summer',
    'autumn',
    'autumn',
    'autumn',
    'winter',
  ];
  const s = north[(month - 1 + 12) % 12]!;
  if (!southern) return s;
  return ({ winter: 'summer', summer: 'winter', spring: 'autumn', autumn: 'spring' } as const)[s];
}

export interface StatsAirport extends LatLon {
  code: string;
  city: string | null;
  country: string | null;
  /** Sovereign country (US territories fold into the US). */
  countryCode: string | null;
}

/** One past flight, read once. */
export interface FlightRecord {
  id: string;
  airline: string;
  /** Airline name, or what the booking called it. */
  airlineName: string;
  flightNumber: string;
  from: StatsAirport | null;
  to: StatsAirport | null;
  fromCode: string;
  toCode: string;
  departAt: number;
  /** Local departure date and parts at the origin. */
  date: string;
  year: number;
  month: number;
  weekday: number;
  hour: number;
  minutes: number | null;
  miles: number | null;
  cancelled: boolean;
  international: boolean;
  cabin: CabinClass | null;
  seat: SeatPosition | null;
  aircraft: string | null;
  /** Arrival delay in minutes from live data (departure delay if arrival is missing). */
  delay: number | null;
}

export interface FlightStatsInput {
  items: Item[];
  places: PlaceLookup;
  live?: ReadonlyMap<string, FlightLive>;
  now: number;
}

function airportOf(places: PlaceLookup, placeId: string | null, code: string) {
  const p = places(placeId);
  if (!p || p.lat == null || p.lon == null) return null;
  return {
    code: p.iata ?? code,
    city: p.city,
    country: p.country,
    countryCode: p.countryCode ? sovereignOf(p.countryCode) : null,
    lat: p.lat,
    lon: p.lon,
  } satisfies StatsAirport;
}

/** Flights that have landed by `now` (cancelled ones included, flagged), oldest first. */
export function flightRecords({ items, places, live, now }: FlightStatsInput): FlightRecord[] {
  const out: FlightRecord[] = [];
  for (const i of items) {
    if (i.deleted || i.type !== 'flight' || i.startAt == null) continue;
    if ((i.endAt ?? i.startAt) > now) continue;
    const d = i.details as FlightDetails;
    const l = live?.get(i.id);
    const from = airportOf(places, i.startPlaceId, d.origin);
    const to = airportOf(places, i.endPlaceId, d.destination);
    const zone = i.startTz ?? places(i.startPlaceId)?.tz;
    const tz = zone && isValidTimeZone(zone) ? zone : 'UTC';
    const p = localParts(i.startAt, tz);
    // Gate to gate: actual times when live data has both, else the booked times.
    const gateOut = l?.actualOut ?? null;
    const gateIn = l?.actualIn ?? null;
    const span =
      gateOut != null && gateIn != null
        ? gateIn - gateOut
        : i.endAt != null
          ? i.endAt - i.startAt
          : null;
    const minutes = span != null && span > 0 && span < 24 * 3_600_000 ? span / 60_000 : null;
    let delay: number | null = null;
    if (l?.scheduledIn != null && l.actualIn != null) delay = (l.actualIn - l.scheduledIn) / 60_000;
    else if (l?.scheduledOut != null && l.actualOut != null)
      delay = (l.actualOut - l.scheduledOut) / 60_000;
    const aircraft = aircraftName(d.aircraft) ?? aircraftName(l?.aircraftType);
    const airline = d.airline.trim().toUpperCase();
    out.push({
      id: i.id,
      airline,
      airlineName: airlineName(airline) ?? (i.provider?.trim() || airline),
      flightNumber: d.flightNumber,
      from,
      to,
      fromCode: from?.code ?? d.origin,
      toCode: to?.code ?? d.destination,
      departAt: i.startAt,
      date: localDate(i.startAt, tz),
      year: p.year,
      month: p.month,
      weekday: p.weekday,
      hour: p.hour,
      minutes,
      miles: from && to ? distanceMi(from, to) : null,
      cancelled: i.status === 'cancelled' || l?.cancelled === true,
      international:
        !!from?.countryCode && !!to?.countryCode && from.countryCode !== to.countryCode,
      cabin: cabinClass(d.cabin),
      seat: seatPosition(d.seat ?? d.passengers?.find((x) => x.isMe)?.seat, aircraft),
      aircraft,
      delay: delay == null ? null : Math.round(delay),
    });
  }
  return out.sort((a, b) => a.departAt - b.departAt);
}

/** Years that have flights, newest first. */
export function flightYears(records: FlightRecord[]): number[] {
  const set = new Set<number>();
  for (const r of records) if (!r.cancelled) set.add(r.year);
  return [...set].sort((a, b) => b - a);
}

export interface Ranked<T = string> {
  key: T;
  count: number;
}

export interface FlightRef {
  id: string;
  airline: string;
  airlineName: string;
  flightNumber: string;
  fromCode: string;
  toCode: string;
  date: string;
  minutes: number | null;
  miles: number | null;
}

export interface RouteStat {
  key: string;
  /** Codes in alphabetical order; a route counts both ways. */
  a: string;
  b: string;
  count: number;
  miles: number | null;
  from: StatsAirport | null;
  to: StatsAirport | null;
}

export interface FlightStats {
  year: number | null;
  flights: number;
  cancelled: number;
  international: number;
  domestic: number;
  minutes: number;
  miles: number;
  /** Times around the Earth at the equator. */
  laps: number;
  /** Trips to the Moon. */
  moons: number;
  avgMinutes: number | null;
  avgMiles: number | null;
  longest: FlightRef | null;
  shortest: FlightRef | null;
  /** Flights per year from the first to the last, per month (12) and per weekday (Sun first). */
  perYear: Ranked<number>[];
  perMonth: number[];
  perWeekday: number[];
  /** Busiest and slowest year (all time) or month (one year). */
  busiest: { label: string; count: number } | null;
  slowest: { label: string; count: number } | null;
  /** Share leaving 06:00 to 18:59 local. */
  daytimeShare: number | null;
  redEyes: number;
  seasons: Record<Season, number>;
  topSeason: { season: Season; share: number } | null;
  topWeekday: { weekday: number; share: number } | null;
  delays: {
    /** Flights with live timing to compare. */
    timed: number;
    early: number;
    onTime: number;
    delayed: number;
    cancelled: number;
    /** Minutes late, summed over the delayed flights. */
    totalMinutes: number;
    worst: (FlightRef & { delay: number }) | null;
  };
  aircraft: Ranked[];
  alliances: Record<Alliance, number>;
  routes: RouteStat[];
  cabins: Record<CabinClass, number>;
  cabinKnown: number;
  seats: Record<SeatPosition, number>;
  seatKnown: number;
  airlines: (Ranked & { name: string })[];
  airports: (Ranked & { airport: StatsAirport | null })[];
  countries: (Ranked & { continent: Continent | null; name: string | null })[];
  continents: { continent: Continent; visited: number; total: number }[];
}

/** Early is 5+ minutes ahead of schedule; delayed is 15+ minutes late, as airlines count it. */
export const EARLY_MIN = -5;
export const LATE_MIN = 15;

const MONTH_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function ref(r: FlightRecord): FlightRef {
  return {
    id: r.id,
    airline: r.airline,
    airlineName: r.airlineName,
    flightNumber: r.flightNumber,
    fromCode: r.fromCode,
    toCode: r.toCode,
    date: r.date,
    minutes: r.minutes,
    miles: r.miles,
  };
}

function rank<T>(m: Map<string, T & { count: number }>): (T & { count: number })[] {
  return [...m.values()].sort((a, b) => b.count - a.count);
}

function bump<T extends { count: number }>(
  m: Map<string, T>,
  key: string,
  make: () => Omit<T, 'count'>,
) {
  const hit = m.get(key);
  if (hit) hit.count++;
  else m.set(key, { ...make(), count: 1 } as T);
}

/** Adds up flights for all time (year null) or one year. Cancelled flights only count as such. */
export function flightStats(
  records: FlightRecord[],
  year: number | null,
  now: number,
): FlightStats {
  const scoped = year == null ? records : records.filter((r) => r.year === year);
  const flown = scoped.filter((r) => !r.cancelled);
  const cancelled = scoped.length - flown.length;

  let minutes = 0;
  let miles = 0;
  let timedMinutes = 0;
  let measured = 0;
  let international = 0;
  let daytime = 0;
  let redEyes = 0;
  let longest: FlightRecord | null = null;
  let shortest: FlightRecord | null = null;
  const perMonth = Array<number>(12).fill(0);
  const perWeekday = Array<number>(7).fill(0);
  const years = new Map<number, number>();
  const seasons: Record<Season, number> = { spring: 0, summer: 0, autumn: 0, winter: 0 };
  const cabins: Record<CabinClass, number> = { economy: 0, premium: 0, business: 0, first: 0 };
  const seats: Record<SeatPosition, number> = { window: 0, middle: 0, aisle: 0 };
  const alliances: Record<Alliance, number> = { oneworld: 0, 'Star Alliance': 0, SkyTeam: 0 };
  const aircraft = new Map<string, Ranked>();
  const airlines = new Map<string, Ranked & { name: string }>();
  const airports = new Map<string, Ranked & { airport: StatsAirport | null }>();
  const countries = new Map<
    string,
    Ranked & { continent: Continent | null; name: string | null }
  >();
  const routes = new Map<string, RouteStat>();
  const delays = {
    timed: 0,
    early: 0,
    onTime: 0,
    delayed: 0,
    cancelled,
    totalMinutes: 0,
    worst: null as (FlightRef & { delay: number }) | null,
  };

  const length = (r: FlightRecord) => r.miles ?? r.minutes ?? 0;

  for (const r of flown) {
    if (r.minutes != null) {
      minutes += r.minutes;
      timedMinutes++;
    }
    if (r.miles != null) {
      miles += r.miles;
      measured++;
    }
    if (r.international) international++;
    if (r.hour >= 6 && r.hour <= 18) daytime++;
    // Red-eye: leaves late and flies through the night.
    if ((r.hour >= 21 || r.hour < 3) && (r.minutes ?? 0) >= 150) redEyes++;
    if (r.miles != null || r.minutes != null) {
      if (!longest || length(r) > length(longest)) longest = r;
      if (!shortest || length(r) < length(shortest)) shortest = r;
    }
    perMonth[r.month - 1]!++;
    perWeekday[r.weekday]!++;
    years.set(r.year, (years.get(r.year) ?? 0) + 1);
    seasons[seasonOf(r.month, (r.from?.lat ?? 0) < 0)]++;
    if (r.cabin) cabins[r.cabin]++;
    if (r.seat) seats[r.seat]++;
    const alliance = allianceOf(r.airline);
    if (alliance) alliances[alliance]++;
    if (r.aircraft) bump(aircraft, r.aircraft, () => ({ key: r.aircraft! }));
    bump(airlines, r.airline, () => ({
      key: r.airline,
      name: r.airlineName,
    }));
    for (const [code, a] of [
      [r.fromCode, r.from],
      [r.toCode, r.to],
    ] as const) {
      bump(airports, code, () => ({ key: code, airport: a }));
    }
    const ccs = new Set([r.from?.countryCode, r.to?.countryCode].filter((c) => !!c) as string[]);
    for (const cc of ccs) {
      const name = r.from?.countryCode === cc ? r.from.country : (r.to?.country ?? null);
      bump(countries, cc, () => ({ key: cc, continent: continentOf(cc), name }));
    }
    const [a, b] = [r.fromCode, r.toCode].sort();
    const key = `${a}-${b}`;
    const flipped = a !== r.fromCode;
    bump(routes, key, () => ({
      key,
      a: a!,
      b: b!,
      miles: r.miles,
      from: flipped ? r.to : r.from,
      to: flipped ? r.from : r.to,
    }));
    if (r.delay != null) {
      delays.timed++;
      if (r.delay <= EARLY_MIN) delays.early++;
      else if (r.delay < LATE_MIN) delays.onTime++;
      else {
        delays.delayed++;
        delays.totalMinutes += r.delay;
      }
      if (r.delay >= LATE_MIN && (!delays.worst || r.delay > delays.worst.delay))
        delays.worst = { ...ref(r), delay: r.delay };
    }
  }

  // Busiest and slowest: years for all time, months for one year (only months so far).
  let perYear: Ranked<number>[] = [];
  let busiest: FlightStats['busiest'] = null;
  let slowest: FlightStats['slowest'] = null;
  if (year == null) {
    const ys = [...years.keys()];
    if (ys.length) {
      for (let y = Math.min(...ys); y <= Math.max(...ys); y++)
        perYear.push({ key: y, count: years.get(y) ?? 0 });
    }
    const sorted = [...perYear].sort((x, y) => y.count - x.count || y.key - x.key);
    if (sorted.length) busiest = { label: String(sorted[0]!.key), count: sorted[0]!.count };
    // The year under way isn't over, so it can't be the quietest yet.
    const done = sorted.slice(1).filter((y) => y.key !== new Date(now).getUTCFullYear());
    const last = done[done.length - 1];
    if (last) slowest = { label: String(last.key), count: last.count };
  } else if (flown.length) {
    perYear = [{ key: year, count: flown.length }];
    const thisYear = new Date(now).getUTCFullYear();
    const months = year === thisYear ? new Date(now).getUTCMonth() + 1 : 12;
    const list = perMonth.slice(0, months).map((count, m) => ({ m, count }));
    const sorted = [...list].sort((x, y) => y.count - x.count || x.m - y.m);
    busiest = { label: MONTH_LONG[sorted[0]!.m]!, count: sorted[0]!.count };
    // Likewise the month under way.
    const done = sorted.slice(1).filter((x) => year !== thisYear || x.m !== months - 1);
    const last = done[done.length - 1];
    if (last) slowest = { label: MONTH_LONG[last.m]!, count: last.count };
  }

  const n = flown.length;
  const topOf = <K extends string | number>(entries: [K, number][]) => {
    const [k, c] = entries.reduce((best, e) => (e[1] > best[1] ? e : best), entries[0]!);
    return n && c ? { k, share: c / n } : null;
  };
  const season = topOf(Object.entries(seasons) as [Season, number][]);
  const weekday = topOf(perWeekday.map((c, i) => [i, c] as [number, number]));

  const visited = new Map<Continent, number>();
  for (const c of countries.values())
    if (c.continent) visited.set(c.continent, (visited.get(c.continent) ?? 0) + 1);

  return {
    year,
    flights: n,
    cancelled,
    international,
    domestic: n - international,
    minutes,
    miles,
    laps: miles / EARTH_CIRCUMFERENCE_MI,
    moons: miles / MOON_MI,
    avgMinutes: timedMinutes ? minutes / timedMinutes : null,
    avgMiles: measured ? miles / measured : null,
    longest: longest ? ref(longest) : null,
    shortest: shortest && shortest !== longest ? ref(shortest) : null,
    perYear,
    perMonth,
    perWeekday,
    busiest,
    slowest,
    daytimeShare: n ? daytime / n : null,
    redEyes,
    seasons,
    topSeason: season ? { season: season.k, share: season.share } : null,
    topWeekday: weekday ? { weekday: weekday.k, share: weekday.share } : null,
    delays,
    aircraft: rank(aircraft),
    alliances,
    routes: rank(routes),
    cabins,
    cabinKnown: cabins.economy + cabins.premium + cabins.business + cabins.first,
    seats,
    seatKnown: seats.window + seats.middle + seats.aisle,
    airlines: rank(airlines),
    airports: rank(airports),
    countries: rank(countries),
    continents: [...visited.entries()]
      .map(([continent, v]) => ({ continent, visited: v, total: CONTINENT_TOTALS[continent] }))
      .sort((a, b) => b.visited - a.visited),
  };
}
